import { RequestOptions } from '@web-libs/core/http/api/http.types';
import { addQueryString } from '@web-libs/core/http/infrastructure/shared/_build_query_params';
import { buildUrl } from '@web-libs/core/http/infrastructure/shared/_build_url';
import { parseResponse } from '@web-libs/core/http/infrastructure/shared/_parse-response';
import {
  buildFetchOptions,
  RequestInitTransformer
} from '@web-libs/core/http/infrastructure/shared/_build_fetch_options';
import { FetchStatusError, handleError } from '@web-libs/core/http/infrastructure/shared/_error-handling';
import { ResponseInterceptor } from '@web-libs/core/http/infrastructure/shared/shared.types';

let id = 0;

const logOnServer = (...args: any[]) => {
  if(typeof window === 'undefined') {
    // Server-side logging removed
  }
}

export const buildFetchFunction = (baseUrl: string, requestInitTransformer: RequestInitTransformer[], responseInterceptors?: ResponseInterceptor[]) =>
  async (method: string, url: string, body?: any, requestOptions?: RequestOptions) => {
    const builtUrl = buildUrl(url, baseUrl!);
    const urlWithQueryParams = addQueryString(builtUrl, requestOptions?.queryParams);
    const builtOptions = buildFetchOptions(method, body, requestOptions, requestInitTransformer);
    try {
      const requestId = id++;
      logOnServer(`Request to (${requestId}): `, urlWithQueryParams, builtOptions);
      const rawResponse = await fetch(urlWithQueryParams, builtOptions)
      logOnServer(`Response from (${builtUrl})(${requestId}): `, rawResponse);
      const requestData: RequestData = {
        url: urlWithQueryParams,
        requestOptions: requestOptions || {},
        requestInit: builtOptions,
        retry: (url: string, reqInit: RequestInit) => fetch(url, reqInit),
        retryWhole: () => buildFetchFunction(baseUrl, requestInitTransformer, responseInterceptors)(method, url, body, requestOptions)
      }
      const response = await interceptResponse(rawResponse, responseInterceptors || [], requestData);
      const preHandledResponse = await preHandleResponse(response);
      const parsedResponse = await parseResponse(preHandledResponse);
      logOnServer(`Parsed Response (${builtUrl})(${requestId}): `, parsedResponse);
      return parsedResponse;
    } catch (error) {
      // Error logging removed
      if (typeof window === 'undefined') {
        // Server-side error logging removed
      }
      handleError(error);
    }
  };

const interceptResponse = (response: Response, interceptors: ResponseInterceptor[], requestData: RequestData) => {
  return interceptors.reduce(async (prevResponse, interceptor) => {
    const response = await prevResponse;
    return await interceptor(response, requestData);
  }, Promise.resolve(response));
};


const preHandleResponse = async (response: Response) => {
  if (!response.ok) {
    // Response logging removed
    const responseBody = await response.clone().text();
    throw new FetchStatusError(response.status, responseBody);
  }
  return response;
};

export type RequestData = {
  url: string, //with query params
  requestOptions: RequestOptions,
  requestInit: RequestInit
  retry: (url: string, reqInit: RequestInit) => Promise<Response>
  retryWhole: () => Promise<Response>
}
